import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  Plus, Search, RefreshCw, Pencil, ChevronRight, HardHat, ShieldCheck,
  Loader2, X, Power, AlertTriangle, Upload,
} from "lucide-react";
import { FormDialog, Field } from "./assets/AssetForms";
import CsvImportDialog from "./assets/CsvImportDialog";

const STATUS_TONE = {
  PREFERRED:   "bg-[#064E2E] text-white",
  APPROVED:    "bg-[#064E2E]/15 text-[#064E2E]",
  MONITOR:     "bg-[#B88A2A]/15 text-[#B88A2A]",
  RESTRICTED:  "bg-[#B91C1C]/15 text-[#B91C1C]",
  DISABLED:    "bg-[#475569]/20 text-[#475569]",
  UNRATED:     "bg-[#94A3B8]/15 text-[#475569]",
};

const STATUS_OPTIONS = ["", "PREFERRED", "APPROVED", "MONITOR", "RESTRICTED", "DISABLED"];

/* Operational contractor directory. Lightweight by design — NOT a CAFM. */
export default function ContractorDirectory() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/contractors");
      setRows(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const disable = async (c) => {
    if (!window.confirm(`Disable ${c.name}? They will no longer be selectable from jobs.`)) return;
    try {
      await adminApi.post(`/contractors/${c.id}/disable`);
      toast.success(`${c.name} disabled`);
      load();
    } catch (e) { toast.error("Failed to disable"); }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter && (r.approved_status || r.effective_status || "").toUpperCase() !== statusFilter) return false;
    if (search && !`${r.name} ${r.primary_trade || ""} ${r.coverage_area || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="contractor-directory-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Operations · Directory</p>
          <h1>Contractors</h1>
          <p className="lead">Every contractor PHS works with. Add, edit, or disable from one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} className="text-sm h-10 px-4 rounded-md border border-[#E2DCD0] text-[#071A33] hover:border-[#0F766E] hover:text-[#0F766E] inline-flex items-center gap-1.5" data-testid="contractors-import-btn">
            <Upload size={14}/> Bulk import CSV
          </button>
          <button onClick={() => setEditing({})} className="text-sm h-10 px-4 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1.5" data-testid="contractors-add-btn">
            <Plus size={14}/> Add Contractor
          </button>
          <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="contractors-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        </div>
      </div>

      <div className="admin-panel p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-[#475569]"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, trade, coverage…" className="field-input m-0 h-9 text-sm flex-1" data-testid="contractors-search"/>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="field-input h-9 text-sm m-0" data-testid="contractors-status-filter">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>

      <div className="admin-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
            <tr className="text-left">
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Trade</th>
              <th className="px-4 py-3">Coverage</th>
              <th className="px-4 py-3 text-center">Health</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#475569] italic">No contractors yet. Click "Add Contractor".</td></tr>
            )}
            {filtered.map((c) => {
              const status = (c.approved_status || c.effective_status || "APPROVED").toUpperCase();
              const tone = STATUS_TONE[status] || STATUS_TONE.APPROVED;
              return (
                <tr key={c.id} className="hover:bg-[#F7F4ED]/50" data-testid={`contractor-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-[#071A33]"><HardHat size={12} className="inline text-[#0F766E] mr-2"/>{c.name}</td>
                  <td className="px-4 py-3 text-[#475569]">
                    {c.contact_name && <div>{c.contact_name}</div>}
                    {c.email   && <div className="text-[11px]">{c.email}</div>}
                    {c.phone   && <div className="text-[11px]">{c.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-[#475569]">{c.primary_trade || "—"}</td>
                  <td className="px-4 py-3 text-[#475569]">{c.coverage_area || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-9 h-7 rounded-md text-[11px] font-semibold tabular-nums ${
                      c.health_score == null ? "bg-[#94A3B8]/15 text-[#475569]"
                      : c.health_score >= 70 ? "bg-[#064E2E] text-white"
                      : c.health_score >= 50 ? "bg-[#B88A2A] text-white"
                      : "bg-[#B91C1C] text-white"
                    }`}>{c.health_score ?? "—"}</span>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded ${tone}`}>{status}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{c.active_jobs ?? 0}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(c)} className="text-[12px] text-[#475569] hover:text-[#064E2E] mr-2" data-testid={`contractor-edit-${c.id}`}><Pencil size={13}/></button>
                    {status !== "DISABLED" && (
                      <button onClick={() => disable(c)} className="text-[12px] text-[#B91C1C] hover:underline inline-flex items-center gap-1" data-testid={`contractor-disable-${c.id}`}><Power size={11}/>Disable</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && <div className="px-4 py-6 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={14}/></div>}
      </div>

      <ContractorFormDialog
        contractor={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <CsvImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onComplete={() => load()}
        title="Bulk import Contractors"
        endpoint="/contractors/import"
        templateFilename="contractors-template.csv"
        templateHeaders={["name", "primary_trade", "coverage_area", "approved_status", "phone", "email", "notes"]}
        templateExampleRow={["AB Roofing Solutions", "Roofing", "National", "PREFERRED", "01234 567890", "ops@example.co.uk", "Trusted partner"]}
        testid="contractors-csv-import"
      />
    </div>
  );
}

function ContractorFormDialog({ contractor, onClose, onSaved }) {
  const isEdit = contractor && contractor.id;
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!contractor) return;
    setDraft({
      name: contractor.name || "",
      contact_name: contractor.contact_name || "",
      phone: contractor.phone || "",
      email: contractor.email || "",
      primary_trade: contractor.primary_trade || "",
      coverage_area: contractor.coverage_area || "",
      approved_status: contractor.approved_status || "APPROVED",
      notes: contractor.notes || "",
    });
  }, [contractor]);

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (isEdit) await adminApi.patch(`/contractors/${contractor.id}`, draft);
      else        await adminApi.post("/contractors", draft);
      toast.success(isEdit ? "Contractor updated" : "Contractor created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <FormDialog open={!!contractor} onClose={onClose} title={isEdit ? `Edit · ${contractor?.name}` : "Add Contractor"} onSubmit={save} busy={busy} submitLabel={isEdit ? "Save changes" : "Create contractor"} testid="contractor-form-dialog">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Company name *" full>
          <input className="field-input m-0 h-10" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required data-testid="contractor-name-input"/>
        </Field>
        <Field label="Contact name">
          <input className="field-input m-0 h-10" value={draft.contact_name || ""} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} data-testid="contractor-contact-input"/>
        </Field>
        <Field label="Phone">
          <input className="field-input m-0 h-10" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })}/>
        </Field>
        <Field label="Email" full>
          <input type="email" className="field-input m-0 h-10" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })}/>
        </Field>
        <Field label="Primary trade">
          <input className="field-input m-0 h-10" value={draft.primary_trade || ""} onChange={(e) => setDraft({ ...draft, primary_trade: e.target.value })} placeholder="e.g. Roofing & rainwater"/>
        </Field>
        <Field label="Coverage area">
          <input className="field-input m-0 h-10" value={draft.coverage_area || ""} onChange={(e) => setDraft({ ...draft, coverage_area: e.target.value })} placeholder="e.g. Essex, Suffolk"/>
        </Field>
        <Field label="Approved status" full>
          <select className="field-input m-0 h-10" value={draft.approved_status} onChange={(e) => setDraft({ ...draft, approved_status: e.target.value })} data-testid="contractor-status-select">
            {STATUS_OPTIONS.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Notes" full>
          <textarea rows={3} className="field-input m-0 py-2" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}/>
        </Field>
      </div>
    </FormDialog>
  );
}
